import React, { useState } from 'react';
import { createContent, updateContent } from '../../services/api';
import { FormInput } from '../components/FormInput';
import { useImageUpload } from '../hooks/useImageUpload';
import { getFullImageUrl } from '../utils/helpers';
import toast from 'react-hot-toast';

const ContentForm = ({ editingItem, onClose, onRefresh }) => {
  const [formData, setFormData] = useState({
    sectionId: editingItem?.sectionId || '',
    title: editingItem?.title || '',
    subtitle: editingItem?.subtitle || '',
    content: editingItem?.content || '',
    imageUrl: editingItem?.imageUrl || '',
    buttonText: editingItem?.buttonText || '',
    buttonLink: editingItem?.buttonLink || '',
    isActive: editingItem?.isActive !== false
  });
  const [loading, setLoading] = useState(false);
  const { imageFile, uploading, preview, handleImageChange, uploadImage } = useImageUpload();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sectionId) {
      toast.error('Section ID is required');
      return;
    }
    
    setLoading(true);
    let imageUrl = formData.imageUrl;
    if (imageFile) imageUrl = await uploadImage();
    
    const submitData = { 
      ...formData, 
      imageUrl, 
      updatedAt: Date.now(),
      isActive: formData.isActive !== false
    };
    
    try {
      if (editingItem && editingItem._id) {
        await updateContent(editingItem._id, submitData);
        toast.success('Content updated successfully');
      } else {
        await createContent(submitData);
        toast.success('Content created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error(error.message || 'Error saving content');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '650px', width: '90%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Content' : 'Add New Content'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <FormInput 
            label="Section ID" 
            name="sectionId" 
            value={formData.sectionId} 
            onChange={(e) => setFormData({ ...formData, sectionId: e.target.value })} 
            placeholder="e.g., about, services, products, whyus" 
            required 
          />
          <FormInput 
            label="Title" 
            name="title" 
            value={formData.title} 
            onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
            placeholder="Section title" 
            required 
          />
          <FormInput 
            label="Subtitle" 
            name="subtitle" 
            value={formData.subtitle} 
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} 
            placeholder="Section subtitle" 
          />
          <FormInput 
            label="Content" 
            name="content" 
            type="textarea" 
            rows="6" 
            value={formData.content} 
            onChange={(e) => setFormData({ ...formData, content: e.target.value })} 
            placeholder="Main content text" 
          />
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Image (Optional)</label>
            {preview && <img src={preview} alt="Preview" style={{ width: '80px', marginBottom: '0.5rem', display: 'block' }} />}
            {formData.imageUrl && !preview && <img src={getFullImageUrl(formData.imageUrl)} alt="Current" style={{ width: '80px', marginBottom: '0.5rem', display: 'block' }} />}
            <input type="file" accept="image/*" onChange={(e) => handleImageChange(e.target.files[0])} style={{ width: '100%', marginBottom: '0.5rem' }} />
            <input 
              type="text" 
              value={formData.imageUrl} 
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} 
              placeholder="Image URL" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginTop: '0.5rem' }} 
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormInput 
              label="Button Text" 
              name="buttonText" 
              value={formData.buttonText} 
              onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })} 
              placeholder="Learn More" 
            />
            <FormInput 
              label="Button Link" 
              name="buttonLink" 
              value={formData.buttonLink} 
              onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })} 
              placeholder="#contact" 
            />
          </div>
          
          <FormInput 
            label="Active" 
            name="isActive" 
            type="checkbox" 
            value={formData.isActive} 
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} 
          />
          
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" disabled={loading || uploading} style={{ flex: 1, padding: '0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              {loading || uploading ? 'Saving...' : 'Save Content'}
            </button>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.75rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentForm;